<template>
  <div class="about-page" :class="{ 'is-en': $i18n.locale === 'en-US' }">
    <!-- ========== Banner 区域 ========== -->
    <div class="banner-section">
      <img
        v-lazy="require('@/assets/manage-bg.png')"
        class="banner-bg"
        :alt="$t('丰派收银')"
      />
      <div class="banner-text">
        <h1 class="banner-title">{{ $t('零售批发SAAS解决方案') }}</h1>
        <p class="banner-subtitle">{{ $t('加速行业数字化升级') }}</p>
        <p class="banner-desc">
          {{ $t('提供企业、门店、小程序、零售批发、运营管理等行业解决方案') }}<br/>
          {{ $t('全方位服务互联网、企业、零售批发业等用户群体，让拓客经营更精准高效') }}
        </p>
      </div>
    </div>

    <!-- ========== 子页面 Tab 导航栏 ========== -->
    <div class="sub-tab-nav">
      <div class="nav-inner">
        <button
          v-for="tab in subTabs"
          :key="tab.key"
          type="button"
          class="tab-item"
          :class="{ active: activeSubTab === tab.key }"
          @click="scrollToSection(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>
    </div>

    <!-- ========== 公司介绍 区域 ========== -->
    <div id="section-intro" ref="sectionIntro" class="intro-section">
      <div class="intro-content">
        <div class="intro-left">
          <h2 class="section-heading-lg">{{ $t('公司介绍') }}</h2>
          <div class="intro-texts">
            <p class="intro-para">
              {{ $t('东莞市丰派软件科技有限公司是国内一家从事SaaS服务的企业，拥有多项核心自主知识产权，专注于为企业提供全渠道零售批发一站式数字化解决方案，助力企业在零售批发领域实现数字化转型。') }}
            </p>
            <p class="intro-para">
              {{ $t('产品体系丰富，覆盖收银软件、订货商城、零售端小程序、智能AI、大数据BI、扫码点餐等业务产品。数十年的行业互联网运营经验，服务于上千家企业级用户和数十万终端客户。') }}
            </p>
          </div>
        </div>
        <div class="intro-right">
          <img
            v-lazy="require('@/assets/introduceContent1.png')"
            class="intro-img"
            :alt="$t('公司介绍')"
          />
        </div>
      </div>
    </div>

    <!-- ========== 企业文化 区域 ========== -->
    <div id="section-culture" ref="sectionCulture" class="culture-section">
      <h2 class="section-heading-xl">{{ $t('企业文化') }}</h2>
      <div class="culture-cards">
        <div
          v-for="(card, idx) in cultureCards"
          :key="'cc-' + idx"
          class="culture-card"
        >
          <img v-lazy="card.icon" class="culture-icon" :alt="card.title" />
          <h3 class="culture-card-title">{{ card.title }}</h3>
          <p class="culture-card-desc">{{ card.desc }}</p>
        </div>
      </div>
    </div>

    <!-- ========== 荣誉资质/品牌认证 区域 ========== -->
    <div id="section-cert" ref="sectionCert" class="cert-section">
      <h2 class="section-heading-xl cert-title">{{ $t('获得国内外多项权威机构认证、品牌认证，丰派值得信任') }}</h2>
      <p class="cert-subtitle">{{ $t('凭借规范的管理、优秀的产品服务及稳定的技术服务能力，丰派获得多个权威认证') }}</p>

      <!-- Tab 切换 -->
      <div class="cert-tabs">
        <button
          v-for="ct in certTabs"
          :key="ct.key"
          type="button"
          class="cert-tab-btn"
          :class="{ active: activeCertTab === ct.key }"
          @click="activeCertTab = ct.key"
        >
          {{ ct.label }}
        </button>
      </div>

      <!-- 证书网格 -->
      <!-- 权威资质：8张固定显示，无展开/收起 -->
      <div v-show="activeCertTab === 'auth'" class="cert-grid cert-auth-grid cert-grid-expanded">
        <div
          v-for="(item, ai) in authCerts"
          :key="'auth-' + ai"
          class="cert-card"
        >
          <img v-lazy="item.img" class="cert-card-img" :alt="item.name" />
          <div class="cert-card-name">{{ item.name }}</div>
        </div>
      </div>

      <!-- 品牌荣誉：初始2行(8张)，可展开查看全部15张 -->
      <div v-show="activeCertTab === 'brand'" class="cert-grid" :class="{ 'cert-grid-expanded': certExpanded }">
        <div
          v-for="(item, bi) in displayBrandCerts"
          :key="'brand-' + bi"
          class="cert-card"
        >
          <img v-lazy="item.img" class="cert-card-img" :alt="item.name" />
          <div class="cert-card-name">{{ item.name }}</div>
        </div>
      </div>

      <!-- 展开/收起按钮（仅品牌荣誉Tab显示） -->
      <button
        v-if="activeCertTab === 'brand'"
        type="button"
        class="cert-toggle-btn"
        @click="certExpanded = !certExpanded"
      >
        <span>{{ certExpanded ? $t('收起') : $t('展开查看更多') }}</span>
        <img
          :src="certExpanded ? require('@/assets/icon-arrow-up.svg') : require('@/assets/icon-arrow-down.svg')"
          class="toggle-icon"
          alt=""
        />
      </button>
    </div>

    <!-- ========== 发展历程 区域（横向滚动时间线） ========== -->
    <div id="section-timeline" ref="sectionTimeline" class="timeline-section">
      <div class="bg-decoration"></div>

      <div class="header">
        <h1>{{ $t('发展历程') }}</h1>
        <p>{{ $t('从数据建设方法论到服务千行百业，我们始终走在数字化转型的最前沿') }}</p>
      </div>

      <div class="timeline-wrapper">
        <div class="fade-left"></div>
        <div class="fade-right"></div>

        <div
          class="timeline-track"
          :class="{ paused: timelineScrollPaused }"
          @mouseenter="timelineScrollPaused = true"
          @mouseleave="timelineScrollPaused = false"
        >
          <!-- 第一组：真实数据 -->
          <template v-for="(node, ni) in timelineNodes">
            <div v-if="node.position === 'top'" :key="'t-n-' + ni" class="timeline-node top">
              <div class="content-box" :class="{ 'single-event': node.events.length === 1, 'multi-event': node.events.length > 1 }">
                <span class="year">{{ node.year }}</span>
                <template v-if="node.events.length === 1">
                  <h3 class="title">{{ node.events[0].title }}</h3>
                  <p class="description">{{ node.events[0].desc }}</p>
                </template>
                <div v-else class="event-list">
                  <div v-for="(evt, ei) in node.events" :key="'e-' + ei" class="event-item">
                    <div class="event-bullet"></div>
                    <div class="event-content">
                      <div class="event-title">{{ evt.title }}</div>
                      <div class="event-desc">{{ evt.desc }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="connector"></div>
              <div class="timeline-dot"></div>
            </div>

            <div v-else :key="'b-n-' + ni" class="timeline-node bottom">
              <div class="timeline-dot"></div>
              <div class="connector"></div>
              <div class="content-box" :class="{ 'single-event': node.events.length === 1, 'multi-event': node.events.length > 1 }">
                <span class="year">{{ node.year }}</span>
                <template v-if="node.events.length === 1">
                  <h3 class="title">{{ node.events[0].title }}</h3>
                  <p class="description">{{ node.events[0].desc }}</p>
                </template>
                <div v-else class="event-list">
                  <div v-for="(evt, ei) in node.events" :key="'e-' + ei" class="event-item">
                    <div class="event-bullet"></div>
                    <div class="event-content">
                      <div class="event-title">{{ evt.title }}</div>
                      <div class="event-desc">{{ evt.desc }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 循环分隔标记 -->
          <div class="timeline-node loop-marker">
            <div class="loop-dash-left"></div>
            <div class="loop-dash-right"></div>
            <div class="loop-icon">
              <svg viewBox="0 0 24 24"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
            </div>
            <div class="loop-text">{{ $t('回到起点') }}</div>
          </div>

          <!-- 第二组：复制数据（无缝循环） -->
          <template v-for="(node, ni) in timelineNodes">
            <div v-if="node.position === 'top'" :key="'t-c-' + ni" class="timeline-node top">
              <div class="content-box" :class="{ 'single-event': node.events.length === 1, 'multi-event': node.events.length > 1 }">
                <span class="year">{{ node.year }}</span>
                <template v-if="node.events.length === 1">
                  <h3 class="title">{{ node.events[0].title }}</h3>
                  <p class="description">{{ node.events[0].desc }}</p>
                </template>
                <div v-else class="event-list">
                  <div v-for="(evt, ei) in node.events" :key="'ce-' + ei" class="event-item">
                    <div class="event-bullet"></div>
                    <div class="event-content">
                      <div class="event-title">{{ evt.title }}</div>
                      <div class="event-desc">{{ evt.desc }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="connector"></div>
              <div class="timeline-dot"></div>
            </div>

            <div v-else :key="'b-c-' + ni" class="timeline-node bottom">
              <div class="timeline-dot"></div>
              <div class="connector"></div>
              <div class="content-box" :class="{ 'single-event': node.events.length === 1, 'multi-event': node.events.length > 1 }">
                <span class="year">{{ node.year }}</span>
                <template v-if="node.events.length === 1">
                  <h3 class="title">{{ node.events[0].title }}</h3>
                  <p class="description">{{ node.events[0].desc }}</p>
                </template>
                <div v-else class="event-list">
                  <div v-for="(evt, ei) in node.events" :key="'ce-' + ei" class="event-item">
                    <div class="event-bullet"></div>
                    <div class="event-content">
                      <div class="event-title">{{ evt.title }}</div>
                      <div class="event-desc">{{ evt.desc }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- 第二组循环标记 -->
          <div class="timeline-node loop-marker">
            <div class="loop-dash-left"></div>
            <div class="loop-dash-right"></div>
            <div class="loop-icon">
              <svg viewBox="0 0 24 24"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>
            </div>
            <div class="loop-text">{{ $t('回到起点') }}</div>
          </div>
        </div>
      </div>

      <div class="scroll-hint">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        <span>{{ $t('自动横向滚动中，鼠标悬停可暂停') }}</span>
      </div>
    </div>

    <!-- ========== 办公氛围 区域（轮播） ========== -->
    <div id="section-office" ref="sectionOffice" class="office-section">
      <h2 class="office-title">{{ $t('办公氛围') }}</h2>

      <!-- 轮播容器 -->
      <div
        class="office-carousel"
        @mouseenter="pauseOfficeCarousel"
        @mouseleave="resumeOfficeCarousel"
      >
        <!-- 幻灯片列表 -->
        <div class="office-slides" :style="{ transform: `translateX(-${officeCurrent * 100}%)` }">
          <div
            v-for="(slide, si) in officeSlides"
            :key="'slide-' + si"
            class="office-slide"
          >
            <img :src="slide.img" :alt="slide.title" />
          </div>
        </div>

        <!-- 左右切换箭头 -->
        <button
          type="button"
          class="office-arrow office-arrow-prev"
          @click="officePrev"
          :aria-label="$t('上一张')"
        >
          &#10094;
        </button>
        <button
          type="button"
          class="office-arrow office-arrow-next"
          @click="officeNext"
          :aria-label="$t('下一张')"
        >
          &#10095;
        </button>

        <!-- 指示器圆点 -->
        <div class="office-dots">
          <span
            v-for="(_, di) in officeSlides"
            :key="'dot-' + di"
            class="office-dot"
            :class="{ active: di === officeCurrent }"
            @click="officeGoTo(di)"
          ></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { t } from "@/i18n";

export default {
  name: "introduce",
  data() {
    return {
      // 子导航 Tab（锚点跳转）
      subTabs: [
        { key: "intro", label: t("丰派介绍") },
        { key: "culture", label: t("企业文化") },
        { key: "cert", label: t("荣誉资质") },
        { key: "timeline", label: t("发展历程") },
      ],
      activeSubTab: "intro",
      /** 点击子导航触发 smooth 滚动期间，暂不根据滚动改写 activeSubTab */
      subTabClickScrolling: false,

      // 企业文化卡片
      cultureCards: [
        {
          icon: require("@/assets/culture-value.png"),
          title: t("价值观"),
          desc: t("利他、平等、个性、阳光"),
        },
        {
          icon: require("@/assets/culture-vision.png"),
          title: t("愿景"),
          desc: t("成为商家服务领域最值得信赖的伙伴"),
        },
        {
          icon: require("@/assets/culture-mission.png"),
          title: t("使命"),
          desc: t("在追求全体丰派人物质与精神两方面幸福的同时，助力零售企业发展，推动零售行业进步，践行利他心，构造实现零售商业4.0"),
        },
      ],

      // 资质认证 Tab
      certTabs: [
        { key: "auth", label: t("权威资质") },
        { key: "brand", label: t("品牌荣誉") },
      ],
      activeCertTab: "auth",
      certExpanded: false,

      // 权威资质：8张（固定2行，无需展开/收起）
      authCerts: [
        { name: t("丰派云POS系统"), img: require("@/assets/cert-auth-1.png") },
        { name: t("丰派自助收银系统"), img: require("@/assets/cert-auth-2.png") },
        { name: t("丰派微商城平台系统"), img: require("@/assets/cert-auth-3.png") },
        { name: t("丰派云TSM配送管理系统"), img: require("@/assets/cert-auth-4.png") },
        { name: t("高新技术企业认证"), img: require("@/assets/cert-brand-1.png") },
        { name: t("丰派购呗购物商城系统"), img: require("@/assets/cert-brand-2.png") },
        { name: t("丰派进销存后台管理系统"), img: require("@/assets/cert-brand-3.png") },
        { name: t("丰派订呗订货商城系统"), img: require("@/assets/cert-brand-4.png") },
      ],

      // 品牌荣誉：15张（初始2行，可展开查看更多）
      brandCerts: [
        { name: t("零售信息化行业优秀代理商"), img: require("@/assets/brand-01.png") },
        { name: t("零售信息化行业优秀代理商"), img: require("@/assets/brand-02.png") },
        { name: t("2026年CCFA金翼零售实践案例"), img: require("@/assets/brand-03.png") },
        { name: t("国美超市--最佳合作伙伴"), img: require("@/assets/brand-04.png") },
        { name: t("苏宁小店--最佳合作伙伴"), img: require("@/assets/brand-05.png") },
        { name: t("全又佳--年度优质供应商"), img: require("@/assets/brand-16.png") },
        { name: t("美佳亲--最佳合作伙伴"), img: require("@/assets/brand-06.png") },
        { name: t("智百威--特别贡献奖"), img: require("@/assets/brand-07.png") },
        { name: t("快乐为民--年度优秀合作商"), img: require("@/assets/brand-08.png") },
        { name: t("民尚--优秀合作伙伴"), img: require("@/assets/brand-09.png") },
        { name: t("阿里之门--优质战略合作伙伴"), img: require("@/assets/brand-10.png") },
        { name: t("趣吃汇--最佳合作伙伴"), img: require("@/assets/brand-11.png") },
        { name: t("快购--年度优质供应商"), img: require("@/assets/brand-12.png") },
        { name: t("惠好佳--年度优质供应商"), img: require("@/assets/brand-13.png") },
        { name: t("悦合集--年度最佳合作伙伴"), img: require("@/assets/brand-14.png") },
        { name: t("好码头--五星优质供应商"), img: require("@/assets/brand-15.png") },
      ],

      // 办公氛围轮播（3张幻灯片：公司、清远团建、松山湖团建）
      officeCurrent: 0,
      officeTimer: null,
      officeSlides: [
        { title: t("公司"), img: require("@/assets/office-slide-1.png") },
        { title: t("清远团建"), img: require("@/assets/office-slide-2.png") },
        { title: t("松山湖团建"), img: require("@/assets/office-slide-3.png") },
      ],

      // 发展历程时间轴节点
      timelineScrollPaused: false,
      timelineNodes: [
        { year: "2017", position: "top", events: [{ title: t("公司成立"), desc: t("公司成立，进军零售行业。") }] },
        { year: "2018", position: "bottom", events: [
          { title: t("向上"), desc: t("公司客户数量快速突破1000+，累计服务终端用户数量突破10000+") },
          { title: t("探索调研"), desc: t("为期数月的终端用户市场调研，深入了解行业痛点") },
        ]},
        { year: "2019", position: "top", events: [{ title: t("业务扩张"), desc: t("组建研发团队，公司整体转型向自主研发。多名资深开发、设计人员加入。") }] },
        { year: "2019", position: "bottom", events: [{ title: t("订货商城"), desc: t("订货商城应用正式上线，获得智百威首轮投资，开启从内向外的服务转型之路。") }] },
        { year: "2020", position: "top", events: [
          { title: t("销售额破亿"), desc: t("订货商城整体销售额破亿，深入零售市场，开始零售商城研发。") },
          { title: t("购呗零售商城上线"), desc: t("零售商城发布,线上企业级客户数破千，终端用户快速突破10w+") },
        ]},
        { year: "2021", position: "bottom", events: [{ title: t("收银软件发布"), desc: t("丰派云POS应用Android/Windows双版本上线，获得市场强烈反响。") }] },
        { year: "2022", position: "top", events: [
          { title: t("收银手机管理APP发布"), desc: t("收银手机管理后台应用上线，超强管理、数据分析、巡检等功能强大。") },
          { title: t("自助收银上线"), desc: t("上线自助收银应用、手机收银功能，满足更多用户场景需求。") },
        ]},
        { year: "2023", position: "bottom", events: [{ title: t("微商城上线"), desc: t("收银微商城小程序上线、线上线下一体化经营，支持定制化品牌首页。") }] },
        { year: "2024", position: "top", events: [{ title: t("收银2.0"), desc: t("丰派云POS2.0全新版本发布，功能整合、交互优化、全新UI、性能升级、更强大。") }] },
        { year: "2025", position: "bottom", events: [{ title: t("业绩突破"), desc: t("丰派云POS合作品牌突破1000+，终端应用上线门店突破10000+") }] },
        { year: "2026", position: "top", events: [{ title: t("智能新纪元"), desc: t("全面进入AI时代，数据智能驱动千行百业数字化转型，开启战略新篇章。") }] },
      ],
    };
  },
  computed: {
    // 品牌荣誉：初始显示8个（2行x4列），展开后显示全部
    displayBrandCerts() {
      return this.certExpanded ? this.brandCerts : this.brandCerts.slice(0, 8);
    },
  },

  mounted() {
    this.bindSubTabScroll();
    this.$nextTick(() => {
      this.startOfficeCarousel();
      this.onSubTabScroll();
    });
  },
  activated() {
    this.bindSubTabScroll();
    this.$nextTick(() => {
      if (this.$wow) this.$wow.init();
      this.startOfficeCarousel();
      this.onSubTabScroll();
    });
  },
  deactivated() {
    this.unbindSubTabScroll();
    this.stopOfficeCarousel();
  },
  beforeDestroy() {
    this.unbindSubTabScroll();
    this.stopOfficeCarousel();
  },
  methods: {
    // ========== 办公氛围轮播 ==========
    startOfficeCarousel() {
      this.stopOfficeCarousel();
      this.officeTimer = setInterval(() => {
        this.officeNext();
      }, 4000);
    },
    stopOfficeCarousel() {
      if (this.officeTimer) {
        clearInterval(this.officeTimer);
        this.officeTimer = null;
      }
    },
    pauseOfficeCarousel() {
      this.stopOfficeCarousel();
    },
    resumeOfficeCarousel() {
      this.startOfficeCarousel();
    },
    officeNext() {
      this.officeCurrent = (this.officeCurrent + 1) % this.officeSlides.length;
    },
    officePrev() {
      this.officeCurrent = (this.officeCurrent - 1 + this.officeSlides.length) % this.officeSlides.length;
    },
    officeGoTo(index) {
      this.officeCurrent = index;
    },
    bindSubTabScroll() {
      if (this._subTabScrollBound) return;
      window.addEventListener("scroll", this.onSubTabScroll, { passive: true });
      this._subTabScrollBound = true;
    },
    unbindSubTabScroll() {
      if (!this._subTabScrollBound) return;
      window.removeEventListener("scroll", this.onSubTabScroll);
      this._subTabScrollBound = false;
    },
    onSubTabScroll() {
      if (this.subTabClickScrolling) return;
      const map = {
        intro: "sectionIntro",
        culture: "sectionCulture",
        cert: "sectionCert",
        timeline: "sectionTimeline",
      };
      const order = ["intro", "culture", "cert", "timeline"];
      const offset = 128;
      let active = "intro";
      for (const key of order) {
        const el = this.$refs[map[key]];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= offset) {
          active = key;
        }
      }
      if (this.activeSubTab !== active) {
        this.activeSubTab = active;
      }
    },
    scrollToSection(key) {
      this.subTabClickScrolling = true;
      this.activeSubTab = key;
      const map = {
        intro: "sectionIntro",
        culture: "sectionCulture",
        cert: "sectionCert",
        timeline: "sectionTimeline",
        office: "sectionOffice",
      };
      const el = this.$refs[map[key]];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      window.setTimeout(() => {
        this.subTabClickScrolling = false;
        this.onSubTabScroll();
      }, 700);
    },
  },
};
</script>

<style lang="less" scoped>
/* ==================== 全局容器 ==================== */
.about-page {
  width: 100%;
  min-width: 1420px;
  background: #ffffff;
}

/* ==================== Banner 区域 ==================== */
.banner-section {
  width: 100%;
  position: relative;
  overflow: hidden;

  .banner-bg {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }

  .banner-text {
    position: absolute;
    left: 272px;
    top: 224px;
    width: 720px;
  }

  .banner-title {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 700;
    font-size: 56px;
    line-height: 64px; /* 56 * 1.142857 ≈ 64 */
    color: #002864;
  }

  .banner-subtitle {
    margin: 16px 0 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 500;
    font-size: 28px;
    line-height: 40px; /* 28 * 1.428571 ≈ 40 */
    color: #002864;
  }

  .banner-desc {
    margin: 12px 0 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 28px; /* 20 * 1.4 = 28 */
    color: #002864;
  }
}

/* ==================== 子页面 Tab 导航栏 ==================== */
.sub-tab-nav {
  width: 100%;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0px 5px 9px 0px rgba(52, 94, 184, 0.2);
  backdrop-filter: blur(8px);

  .nav-inner {
    width: 1000px;
    max-width: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 40px;
    padding: 0;
  }

  .tab-item {
    width: 120px;
    height: 64px;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px; /* 16 * 1.5 */
    color: #69717E; /* 帮助文档color/字体次级颜色 */
    border-bottom: 2px solid transparent;
    transition: color 0.2s, border-color 0.2s;

    &.active {
      color: #343030; /* 帮助文档color/正文 */
      border-bottom-color: #336FFF; /* 官网标椎蓝 */
    }

    &:hover:not(.active) {
      color: #343030;
    }
  }
}

/* ==================== 公司介绍 区域 ==================== */
.intro-section {
  width: 100%;
  background: #FAFCFF;
  padding: 80px 0;
  box-sizing: border-box;

  .intro-content {
    width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: 120px;
  }

  .intro-left {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 40px;
  }

  .section-heading-lg {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 700;
    font-size: 32px;
    line-height: 44px; /* 32 * 1.375 = 44 */
    color: #1E1E23; /* 帮助文档color/H1、H2、H3 */
  }

  .intro-texts {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .intro-para {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 32px; /* 16 * 2 = 32 */
    color: #69717E; /* 帮助文档color/字体次级颜色 */
    text-align: justify;
  }

  .intro-right {
    flex-shrink: 0;
    width: 560px;
    height: 320px;
  }

  .intro-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    display: block;
  }
}

/* ==================== 企业文化 区域 ==================== */
.culture-section {
  width: 100%;
  background: linear-gradient(180deg, #FAFCFF 0%, #EEF4FF 100%);
  padding: 60px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;

  .section-heading-xl {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 700;
    font-size: 36px;
    line-height: 44px; /* 36 * 1.222222... ≈ 44 */
    color: #1E1E23;
  }

  .culture-cards {
    width: 1200px;
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    gap: 24px;
  }

  .culture-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    gap: 10px;
    padding: 20px 40px;
    background: linear-gradient(180deg, #EEF4FF 0%, #FFFFFF 100%);
    border: 2px solid #FFFFFF;
    border-radius: 4px;
    box-shadow: 0px 5px 9px 0px rgba(52, 94, 184, 0.10);
    box-sizing: border-box;
  }

  .culture-icon {
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 4px;
  }

  .culture-card-title {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 500;
    font-size: 24px;
    line-height: 32px; /* 24 * 1.333333 ≈ 32 */
    color: #292929; /* 标准黑16% */
    text-align: justify;
  }

  .culture-card-desc {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 26px; /* 14 * 1.857143 ≈ 26 */
    color: #69717E;
    text-align: justify;
  }
}

/* ==================== 荣誉资质 区域 ==================== */
.cert-section {
  width: 100%;
  background: linear-gradient(180deg, #FFFFFF 0%, #FAFCFF 0%, #EEF4FF 100%);
  padding: 60px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;

  .cert-title {
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 700;
    font-size: 36px;
    line-height: 43px; /* 36 * 1.200000... ≈ 43 */
    color: #1E1E23;
  }

  .cert-subtitle {
    margin: 0;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 18px;
    line-height: 28px; /* 18 * 1.555556 ≈ 28 */
    color: #51565D;
    text-align: center;
  }

  .cert-tabs {
    width: 1200px;
    height: 44px;
    display: flex;
    justify-content: center;
    align-items: stretch;
    gap: 56px;
    border-bottom: 1px solid #E2ECFF;
    box-sizing: border-box;
  }

  .cert-tab-btn {
    width: 200px;
    margin: 0;
    padding: 8px;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-size: 18px;
    line-height: 26px; /* 18 * 1.444444 ≈ 26 */
    color: #1E1E23;
    border-bottom: 2px solid transparent;
    transition: color 0.2s, border-color 0.2s;

    &.active {
      font-weight: 700;
      color: #336FFF; /* 官网标椎蓝 */
      border-bottom-color: #336FFF;
    }

    &:hover:not(.active) {
      opacity: 0.8;
    }
  }

  /* 权威资质网格：固定8张(2行)，无展开/收起 */
  .cert-auth-grid {
    width: 1200px;
    display: flex;
    flex-wrap: wrap;
    justify-content: stretch;
    align-items: stretch;
    gap: 24px;
  }

  /* 品牌荣誉网格：每行4张，flex-wrap自动换行；收起时限制高度隐藏多余行 */
  .cert-grid {
    width: 1200px;
    display: flex;
    flex-wrap: wrap;
    justify-content: stretch;
    align-items: stretch;
    gap: 24px;

    /* 收起状态：只显示前2行（8个），溢出隐藏 */
    &:not(.cert-grid-expanded) {
      max-height: 616px; /* (282+12+12) * 2 = 306*2=612，留点余量 */
      overflow: hidden;
      position: relative;

      /* 底部渐变遮罩提示还有更多内容 */
      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 80px;
        background: linear-gradient(180deg, transparent 0%, #EEF4FF 100%);
        pointer-events: none;
      }
    }
  }

  .cert-card {
    width: calc((1200px - 72px) / 4); /* (1200 - 24*3) / 4 = 282px */
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    background: linear-gradient(180deg, #EEF4FF 0%, #FFFFFF 100%);
    border: 2px solid #FFFFFF;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0px 5px 9px 0px rgba(52, 94, 184, 0.10);
  }

  .cert-card-img {
    width: 282px;
    height: 282px;
    object-fit: cover;
    display: block;
  }

  .cert-card-name {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    padding: 12px 0;
    background: #FFFFFF;
    box-sizing: border-box;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 18px;
    line-height: 22px; /* 18 * 1.2 ≈ 22 */
    color: #292929;
    text-align: justify;
  }

  /* 展开/收起按钮 */
  .cert-toggle-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: 160px;
    height: 40px;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    font-family: "Noto Sans SC", "Source Han Sans CN", sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    color: #69717E; /* 帮助文档color/字体次级颜色 */
    transition: color 0.2s;

    &:hover {
      color: #336FFF;
    }

    .toggle-icon {
      width: 24px;
      height: 24px;
      object-fit: contain;
      transition: transform 0.3s;
    }
  }
}

/* ==================== 发展历程 区域（横向滚动时间线） ==================== */
.timeline-section {
  width: 100%;
  background: #f8faff;
  padding: 60px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;

  /* 背景装饰 */
  .bg-decoration {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -80px; right: -80px;
      width: 400px; height: 400px;
      background: radial-gradient(circle, rgba(51, 111, 255, 0.06) 0%, transparent 70%);
      border-radius: 50%;
    }
    &::after {
      content: '';
      position: absolute;
      bottom: -120px; left: -80px;
      width: 500px; height: 500px;
      background: radial-gradient(circle, rgba(51, 111, 255, 0.04) 0%, transparent 70%);
      border-radius: 50%;
    }
  }

  /* 标题区域 */
  .header {
    text-align: center;
    margin-bottom: 10px;
    position: relative;
    z-index: 1;

    h1 {
      font-size: 24px;
      font-weight: 700;
      color: #336FFF;
      margin-bottom: 16px;
    }
    p {
      font-size: 13px;
      color: #6b7280;
      max-width: 600px;
      margin: 0 auto;
      line-height: 1.2;
    }
  }

  /* 横向滚动轨道容器 */
  .timeline-wrapper {
    position: relative;
    width: 1200px;
    max-width: calc(100% - 40px);
    overflow: hidden;
  }

  /* 轨道：CSS动画驱动无缝循环 */
  .timeline-track {
    display: flex;
    animation: scrollTimeline 50s linear infinite;
    width: max-content;
    position: relative;
    height: 480px;

    &.paused {
      animation-play-state: paused;
    }
  }

  @keyframes scrollTimeline {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  /* 中间贯穿横线 */
  .timeline-track::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 200%;
    height: 2px;
    background: linear-gradient(90deg, #336FFF, #6694ff, #336FFF, #6694ff);
    transform: translateY(-50%);
    z-index: 1;
    pointer-events: none;
  }

  /* ===== 通用节点 ===== */
  .timeline-node {
    flex-shrink: 0;
    width: 340px;
    height: 480px;
    position: relative;
    padding: 0 30px;
  }

  /* 上半部分节点 */
  .top .content-box {
    position: absolute;
    top: 16px;
    left: 30px;
    right: 20px;
    bottom: 258px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    text-align: left;
  }
  .top .connector {
    position: absolute;
    top: 186px;
    left: 18px;
    width: 2px;
    height: 36px;
    background: linear-gradient(to bottom, #336FFF, rgba(51, 111, 255, 0.2));
    transform: translateX(-50%);
  }

  /* 下半部分节点 */
  .bottom .content-box {
    position: absolute;
    top: 288px;
    left: 26px;
    right: 20px;
    bottom: 16px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: left;
  }
  .bottom .connector {
    position: absolute;
    top: 250px;
    left: 18px;
    width: 2px;
    height: 36px;
    background: linear-gradient(to top, #336FFF, rgba(51, 111, 255, 0.2));
    transform: translateX(-50%);
  }

  /* 圆点 */
  .timeline-dot {
    position: absolute;
    top: 50%;
    left: 18px;
    transform: translate(-50%, -50%);
    width: 14px;
    height: 14px;
    background: white;
    border: 3px solid #336FFF;
    border-radius: 50%;
    z-index: 2;
    transition: all 0.3s ease;
    box-shadow: 0 0 0 4px rgba(51, 111, 255, 0.15);

    &:hover {
      background: #336FFF;
      transform: translate(-50%, -50%) scale(1.3);
      box-shadow: 0 0 0 8px rgba(51, 111, 255, 0.2);
    }
  }

  /* 年份标题 */
  .year {
    font-size: 15px;
    font-weight: 700;
    color: #336FFF;
    margin-bottom: 6px;
    display: block;
    transition: color 0.3s;
    line-height: 1.25;
    flex-shrink: 0;
  }

  /* 单事件模式 */
  .single-event .title {
    font-size: 13px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 6px;
    line-height: 1.35;
  }
  .single-event .description {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.65;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* 多事件列表 */
  .event-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow: hidden;
  }
  .event-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 5px 0;

    &:not(:last-child) {
      border-bottom: 1px dashed rgba(51, 111, 255, 0.12);
    }
  }
  .event-bullet {
    width: 6px;
    height: 6px;
    background: #336FFF;
    border-radius: 50%;
    margin-top: 7px;
    flex-shrink: 0;
  }
  .event-content { flex: 1; min-width: 0; }
  .event-title {
    font-size: 11px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1px;
    line-height: 1.35;
  }
  .event-desc {
    font-size: 10px;
    color: #6b7280;
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* 循环分隔标记 */
  .loop-marker {
    width: 160px !important;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 100%;
      height: 6px;
      background: #f8faff;
      transform: translateY(-50%);
      z-index: 2;
    }
  }
  .loop-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 44px;
    height: 44px;
    background: white;
    border: 2px solid #336FFF;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 3;
    transition: all 0.3s ease;

    svg { width: 20px; height: 20px; fill: #336FFF; }

    &:hover {
      background: #336FFF;
      transform: translate(-50%, -50%) scale(1.1);
      box-shadow: 0 4px 16px rgba(51, 111, 255, 0.3);
      svg { fill: white; }
    }
  }
  .loop-text {
    position: absolute;
    top: calc(50% + 30px);
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
    color: #336FFF;
    font-weight: 600;
    letter-spacing: 1px;
    white-space: nowrap;
    z-index: 3;
  }
  .loop-dash-left,
  .loop-dash-right {
    position: absolute;
    top: 50%;
    height: 2px;
    background: repeating-linear-gradient(90deg, #336FFF 0px, #336FFF 3px, transparent 3px, transparent 8px);
    transform: translateY(-50%);
    z-index: 2;
    opacity: 0.3;
  }
  .loop-dash-left { left: 0;   width: 50px; }
  .loop-dash-right { right: 0; width: 50px; }

  /* 左右渐变遮罩 */
  .fade-left,
  .fade-right {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 120px;
    z-index: 10;
    pointer-events: none;
  }
  .fade-left {
    left: 0;
    background: linear-gradient(to right, #f8faff, transparent);
  }
  .fade-right {
    right: 0;
    background: linear-gradient(to left, #f8faff, transparent);
  }

  /* 滚动提示 */
  .scroll-hint {
    text-align: center;
    margin-top: 30px;
    color: #9ca3af;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    position: relative;
    z-index: 1;

    svg {
      width: 20px;
      height: 20px;
      animation: bounceHint 2s infinite;
    }
  }
  @keyframes bounceHint {
    0%, 100% { transform: translateX(0); }
    50% { transform: translateX(8px); }
  }
}

/* ==================== 办公氛围 区域（轮播） ==================== */
.office-section {
  width: 100%;
  background:#F8FAFF;
  padding: 60px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;

  .office-title {
    margin: 0;
    font-family: "Source Han Sans CN", "Noto Sans SC", sans-serif;
    font-weight: 500;
    font-size: 30px;
    line-height: 36px; /* 30 * 1.2 = 36 */
    color: #353535;
  }
}

/* ===== 轮播容器 ===== */
.office-carousel {
  position: relative;
  width: 1360px;
  height: 800px;
  overflow: hidden;
  background: #FFFFFF;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  // 幻灯片轨道
  .office-slides {
    display: flex;
    width: 100%;
    height: 100%;
    transition: transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  // 单张幻灯片
  .office-slide {
    width: 1360px;
    height: 800px;
    flex-shrink: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
    box-sizing: border-box;

    img {
      width: 1280px;
      height: 720px;
      object-fit: cover;
      display: block;
      border-radius: 4px;
    }
  }

  // 左右箭头
  .office-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.88);
    color: #555;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: all 0.3s ease;
    z-index: 10;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

    &:hover {
      background: rgba(255, 255, 255, 0.98);
      color: #333;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
      transform: translateY(-50%) scale(1.05);
    }
  }

  &:hover .office-arrow {
    opacity: 1;
  }

  .office-arrow-prev {
    left: 20px;
  }

  .office-arrow-next {
    right: 20px;
  }

  // 底部指示器（按 Figma: 未激活 8x8 圆角矩形，激活 64x6 横条）
  .office-dots {
    position: absolute;
    bottom: 28px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 10;
  }

  .office-dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: #D9D9D9;
    cursor: pointer;
    transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    &.active {
      width: 64px;   /* Figma: 激活条 64x6 */
      height: 6px;
      border-radius: 3px;
      background: #336FFF;
    }

    &:hover:not(.active) {
      background: #BFBFBF;
    }
  }
}
.about-page.is-en {
  .banner-text {
    width: 480px;
    top: 170px;
  }
  .banner-title {
    font-size: 40px;
    line-height: 50px;
  }
  .banner-subtitle {
    font-size: 22px;
    line-height: 32px;
  }
  .banner-desc {
    font-size: 16px;
    line-height: 26px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pc/introduce.vue